// Smoothly ramp a media element's volume to `target` (0..1) over `ms`, for the
// Story-style fade-in / fade-out of background music on posts. Every write is
// clamped to [0,1] and a per-element generation token invalidates stale chains
// so two ramps can never fight on the same element (which drove volume < 0).
export const rampVolume = (el, target, ms = 450) => {
  if (!el) return;
  try {
    const clamp = (x) => Math.max(0, Math.min(1, x));
    const from = clamp(el.volume);
    const to = clamp(target);
    const gen = (el._fadeGen || 0) + 1;
    el._fadeGen = gen;
    if (el._fadeRaf) { cancelAnimationFrame(el._fadeRaf); el._fadeRaf = null; }
    if (ms <= 0 || Math.abs(to - from) < 0.02) { el.volume = to; return; }
    const start = performance.now();
    const step = (now) => {
      if (el._fadeGen !== gen) return; // superseded by a newer ramp
      const p = Math.min(1, (now - start) / ms);
      el.volume = clamp(from + (to - from) * p);
      if (p < 1) el._fadeRaf = requestAnimationFrame(step);
      else el._fadeRaf = null;
    };
    el._fadeRaf = requestAnimationFrame(step);
  } catch { /* noop */ }
};

// Centralized volume controller for looping background music. Combines three
// factors on every frame while playing: a steady `target` (soft/med/loud), an
// enter/leave `gate` (0..1, animated on scroll in/out) and a time-based
// `envelope` that gently fades the last/first ~0.7s of each loop so the restart
// is smooth. All writes are clamped to [0,1].
export const createMusicFader = (audio, { fade = 0.7 } = {}) => {
  const clamp = (x) => Math.max(0, Math.min(1, x));
  let target = 1;
  let gate = 0;
  let start = 0;
  let gateRaf = null;
  let tickRaf = null;
  let running = false;

  const envelope = () => {
    const d = audio.duration;
    if (!isFinite(d) || d <= fade * 2.2) return 1;
    const remaining = d - audio.currentTime;
    if (remaining <= fade) return clamp(remaining / fade);        // fade out toward loop end
    const since = audio.currentTime - start;
    if (since >= 0 && since <= fade) return clamp(since / fade);  // fade in after the wrap/start
    return 1;
  };
  const apply = () => { try { audio.volume = clamp(target * gate * envelope()); } catch { /* noop */ } };
  const tick = () => { if (!running) return; apply(); tickRaf = requestAnimationFrame(tick); };
  const onPlay = () => { if (!running) { running = true; tick(); } };
  const onPause = () => { running = false; if (tickRaf) { cancelAnimationFrame(tickRaf); tickRaf = null; } };
  audio.addEventListener("play", onPlay);
  audio.addEventListener("pause", onPause);

  const rampGate = (to, ms) => {
    if (gateRaf) cancelAnimationFrame(gateRaf);
    const from = gate;
    const t0 = performance.now();
    const step = (now) => {
      const p = ms <= 0 ? 1 : Math.min(1, (now - t0) / ms);
      gate = from + (to - from) * p;
      apply();
      if (p < 1) gateRaf = requestAnimationFrame(step);
      else gateRaf = null;
    };
    gateRaf = requestAnimationFrame(step);
  };

  return {
    setTarget: (v) => { target = clamp(v); apply(); },
    setStart: (s) => { start = s || 0; },
    setGate: (v) => { if (gateRaf) { cancelAnimationFrame(gateRaf); gateRaf = null; } gate = clamp(v); apply(); },
    fadeIn: (ms = 550) => rampGate(1, ms),
    fadeOut: (ms = 320) => rampGate(0, ms),
    destroy: () => {
      running = false;
      if (tickRaf) cancelAnimationFrame(tickRaf);
      if (gateRaf) cancelAnimationFrame(gateRaf);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    },
  };
};
